import type { FeatureMeasurements } from "./Project";
import type { Resource } from "./Resource";
import type { LabeledResources } from "../sections/orders/Orders";

export enum FeatureType {
  Anchor = "Anchor",
  AutoCover = "AutoCover",
  AutoCoverLiner = "AutoCover Liner",
  Calibration = "Calibration",
  Cove = "Cove",
  Decking = "Decking",
  DivingBoard = "Diving board",
  DivingObject = "Diving object",
  Handrail = "Handrail",
  Liner = "Liner",
  Perimeter = "Perimeter",
  Planter = "Planter",
  SafetyLedge = "Safety Ledge",
  Slide = "Slide",
  Spa = "Spa",
  Steps = "Steps",
  VinylFeature = "Vinyl Feature",
  MoldedStep = "Molded Step",
  Wall = "Wall",
  WallHeight = "Wall Height",
  Other = "Other",
}

export const coverFeatures = [
  FeatureType.AutoCover,
  FeatureType.DivingBoard,
  FeatureType.DivingObject,
  FeatureType.Handrail,
  FeatureType.Planter,
  FeatureType.Slide,
  FeatureType.Spa,
  FeatureType.Steps,
  FeatureType.Wall,
  FeatureType.Other,
  FeatureType.Anchor,
];

export const linerFeatures = [
  FeatureType.AutoCoverLiner,
  FeatureType.Cove,
  FeatureType.Liner,
  FeatureType.SafetyLedge,
  FeatureType.VinylFeature,
  FeatureType.WallHeight,
  FeatureType.MoldedStep,
];

export enum HopperShape {
  Square = "Square",
  Contoured = "Contoured",
  Roman = "Roman",
}

export enum MoldedStepFace {
  Radius = "Radius",
  Straight = "Straight",
  Corner = "Corner",
}

export enum DivingBoardFastenType {
  MoldedBase = "Molded Base",
  TubesOrRails = "Tubes or Rails",
}

export interface Feature {
  pointImageMap?: string;
  measurement?: FeatureMeasurements;
  stepMaterialOption?: string;
  stepMaterial?: string;
  stepFastener?: string;
  stepComment?: string;
  stepStripe?: string;
  stripeWidth?: number;
  differentTreads?: string;
  riserMaterial?: string;
  moldedStepFace?: MoldedStepFace;
  id: string;
  type: FeatureType;
  projectId: string;
  updateTime: Date | null;
  name: string;
  notes: string | null;
  tags: string[] | null;
  isInsidePool?: boolean;
  isRemovable?: boolean;
  isCovered?: boolean;
  scans: any[] | null;
  resources: Resource[] | null;
  length?: number;
  divingBoardFastenType?: DivingBoardFastenType;
  coverExtension?: string;
}

export const labelFeatures = (
  allFeatures: Feature[],
  showAnchor?: boolean,
  showInProject?: boolean,
): { features: LabeledResources[]; decks: LabeledResources[] } => {
  if (allFeatures?.length) {
    let notFoundUntaggedDecking = true;
    const { sortedFeatures, decks } = allFeatures.reduce(
      (acc, feature) => {
        const {
          resources,
          type,
          notes,
          name,
          isRemovable,
          isCovered,
          stepMaterialOption,
          stepMaterial,
          stepFastener,
          stepComment,
          stepStripe,
          stripeWidth,
          moldedStepFace,
          differentTreads,
          riserMaterial,
          id,
          divingBoardFastenType,
        } = feature;
        if (!acc.sortedFeatures[type]) {
          acc.sortedFeatures[type] = [];
        }
        const scannerImages = (resources ?? []).filter(
          (resource) => resource?.type === "scanner image",
        );
        const dxfNumber = name.replace(/\D/g, "");
        let newNotes = notes;
        if (showInProject && notes && notes?.length > 0) {
          const noteList = notes.split(". ");
          if (noteList.length > 0 && noteList[0].length == 0) {
            newNotes = notes?.replace(".", " ");
          }
        }
        if (type === FeatureType.Decking) {
          if (feature?.measurement) {
            acc.decks.push({
              id: id,
              type: type,
              label: `Deck ${dxfNumber}`,
              featureName: name,
              images: scannerImages,
              notes: newNotes ?? undefined,
              length: feature?.measurement?.lengthAlongPerimeterIn ?? undefined,
              measurement: feature?.measurement,
            });
          } else if (notFoundUntaggedDecking) {
            notFoundUntaggedDecking = false;
            acc.decks.push({
              id: id,
              type: type,
              label: `Deck ${dxfNumber}`,
              featureName: name,
              images: scannerImages,
              notes: newNotes ?? undefined,
              length: undefined,
              measurement: feature?.measurement,
            });
          }
        }
        if (
          feature?.measurement &&
          type !== FeatureType.Decking &&
          type !== FeatureType.Calibration &&
          type !== FeatureType.Perimeter &&
          (type !== FeatureType.Anchor || (type === FeatureType.Anchor && showAnchor))
        ) {
          acc.sortedFeatures[type].push({
            id: id,
            label: `${type.replace("Vinyl Feature", "VF")} ${dxfNumber}`,
            type: type,
            featureName: name,
            images: scannerImages,
            notes: newNotes ?? undefined,
            isRemovable: isRemovable,
            stepFastener: stepFastener,
            stepComment: stepComment,
            isCovered: isCovered,
            measurement: feature?.measurement,
            stepMaterialOption: stepMaterialOption,
            stepMaterial: stepMaterial,
            stepStripe: stepStripe,
            stripeWidth: stripeWidth,
            differentTreads: differentTreads,
            riserMaterial: riserMaterial,
            moldedStepFace: moldedStepFace,
            divingBoardFastenType: divingBoardFastenType,
          });
        }
        return acc;
      },
      {
        sortedFeatures: {} as { [key: string]: LabeledResources[] },
        decks: [] as LabeledResources[],
      },
    );
    const features = Object.keys(sortedFeatures).reduce<LabeledResources[]>(
      (acc, key) => {
        return [...acc, ...sortedFeatures[key]];
      },
      [],
    );

    return { features, decks };
  } else {
    return { features: [], decks: [] };
  }
};
